import {
  getPrimaryColor,
  isMobileAtom,
  Styles,
  useRecoil,
} from '@orcar/common-d2c';
import { useSearchParams } from 'react-router-dom';
import {
  ColorText,
  List,
  ListWrapper,
  OrderList,
  OrderListWrapper,
  ParagraphWrapper,
  Section,
  SectionSubTitle,
  Text,
} from '@/components/InformationStyles';
import { ContentWrapper } from '@/components/reservationDetails/commonStyles';
import SeeMoreSection from '@/components/SeeMoreSection';

const app = import.meta.env.VITE_PACKAGE_NAME;

// 대여안내 페이지를 3개 섹션(대여규정 / 예약변경 및 취소 / 자차 특약보험안내)으로 구성.
// 각 섹션은 결제 페이지와 동일한 "미리보기 + 설명 더보기" 방식으로 펼친다.
const RentInfo = () => {
  const { recoilValue: isMobile } = useRecoil(isMobileAtom);
  const [searchParams] = useSearchParams();

  // 헤더/홈에서 ?type=insurance 로 진입하면 자차 특약 섹션을 펼친 상태로 시작한다.
  const isInsuranceDeepLink = searchParams.get('type') === 'insurance';

  return (
    <ContentWrapper>
      <SeeMoreSection title='대여규정' isMobile={isMobile} onGrey>
        <Section isMobile={isMobile}>
          <SectionSubTitle>렌트카 예약안내</SectionSubTitle>
          <ParagraphWrapper>
            <Text>
              실시간 렌트카 예약은 고객님께서 예약 정보를 입력하신 후, 카드결제
              또는 가상계좌로 입금을 하시면 예약이 확정됩니다.
            </Text>
            <Text color={Styles.COLOR.RED}>[인수/반납 가능시간]</Text>
            <Text>인수가능시간: 08:00 ~ 22:00</Text>
            <Text>반납가능시간: 06:00 ~ 20:00</Text>
          </ParagraphWrapper>
          <ParagraphWrapper>
            <Text color={Styles.COLOR.RED}>★새벽 및 야간 배차비 안내★</Text>
            <Text>새벽 06:00~07:30 반납비용 1만원</Text>
            <Text>야간 배차비용 0원</Text>
            <Text>야간 22:00~ 배차불가</Text>
          </ParagraphWrapper>
          <ParagraphWrapper noMargin>
            <Text color={Styles.COLOR.RED}>
              [제주로렌트카 셔틀버스 운행시간 및 탑승 장소]
            </Text>
            <Text>
              제주공항 1층 5번게이트로 나오셔서 횡단보도를 두 번 건넌 후
              렌트카하우스 좌측
            </Text>
            <Text>★8구역3번 승차장★에서 제주로렌트카 셔틀 탑승</Text>
            <Text>[셔틀운행시간]</Text>
            <Text>★공항출발(본사행) 07:50~21:45</Text>
            <Text>★본사출발(공항행) 06:30~20:00</Text>
            <Text>(08~20시까지 15분 간격 순환 운행)</Text>
            <Text>새벽 본사 출발시간: 06:30, 07:00, 07:30</Text>
            <Text>야간 공항 출발시간: 20:15, 20:45, 21:15, 21:45</Text>
            <Text>
              본사에서 공항으로 가는 마지막 셔틀버스는 20시입니다. 20시 이후에는
              개별적으로 이동하셔야 합니다.
            </Text>
            <Text>
              ★마지막 셔틀을 탑승하지 못할 경우 개별적으로 본사로 이동하셔야
              합니다.
            </Text>
            <Text>
              22시까지 인수가 어려울 경우 익일 인수하시기 바랍니다. 비행기
              지연일 경우에도 동일 적용됩니다.
            </Text>
          </ParagraphWrapper>
        </Section>
        <Section isMobile={isMobile}>
          <SectionSubTitle>렌트카 대여 안내</SectionSubTitle>
          <Text>
            렌트카 인수방법 안내(2016년 11월 1일부터 인수장소가 변경되었습니다.)
          </Text>
          <OrderListWrapper>
            <OrderList>
              제주공항 도착 후 5번게이트로 나와 횡단보도를 건너신 후
              우측방향으로 렌트카하우스까지 이동하신 후 제8구역 3번 주차구역에
              세워진 셔틀버스 탑승 후 본사로 이동합니다. (약 10분~15분 소요)
            </OrderList>
            <OrderList>
              당사에서는 차량인수 예정일 전 문자메세지 등을 통해 인수장소를
              안내해드리고 있습니다. 문자메세지에는 셔틀버스 탑승안내장소가
              자세히 안내되어 있으니 꼭 읽어주세요. 문자메세지 수신을 못하신
              고객님은 당사로 연락바랍니다.
            </OrderList>
            <OrderList>
              주차장에서 차량상태 및 연료확인 후 인수(차량인수시 사진 촬영은
              필수)
            </OrderList>
            <OrderList>
              차량 반납은 인수받으신 본사로 하셔야 하며 외부에서는 반납할 수
              없습니다.
            </OrderList>
          </OrderListWrapper>
        </Section>
        <Section isMobile={isMobile}>
          <ParagraphWrapper>
            <SectionSubTitle>대여 자격 안내</SectionSubTitle>
            <ListWrapper>
              <List>
                최소 필요 대여조건은 만 26세 이상 운전경력 만 2년
                이상(장롱면허는 해당되지 않음)으로 운전면허증을 꼭 소지하셔야
                합니다.
              </List>
              <List
                style={{
                  fontWeight: 800,
                }}
              >
                단, 슈퍼카·쏠라티는{' '}
                <ColorText color={Styles.COLOR.RED}>
                  만 30세 이상, 운전경력 3년 이상
                </ColorText>
                이어야 대여 가능합니다.
              </List>
              <List>
                운전 면허 취소 후 재취득 시 운전경력증명서를 발급해 오시면
                대여가능합니다.
              </List>
              <List
                style={{
                  fontWeight: 800,
                }}
              >
                현역군인(장교제외)은 대여불가합니다.(당사지침)
              </List>
              <Text margin='0 0 0 12px'>
                {`★ 내국인 : 도로 교통법상 유효한 국내 운전면허증 또는 국제면허증과 주민등록증 소지자
                ★ 외국인
                한국 면허증을 소지한 외국인은 차량 렌트 가능합니다.
                국제 면허증을 소지한 외국인(한국 면허증 소유하지 않은 외국거주중인 한국인포함)은, `}
                <ColorText color={Styles.COLOR.RED}>
                  {`제2운전자로만 등록`}
                </ColorText>
                {`이 가능하며, `}
                <ColorText color={Styles.COLOR.RED}>
                  {`제1운전자는 필히 한국 면허증을 소지한 분`}
                </ColorText>
                {`으로 등록해야합니다.
                외국인 : 제네바 협약,비엔나 협약, SOFA 가입국에서 발행한 국제운전면허증과 개인여권소지자
                ※ 외국인의 경우 `}
                <ColorText color={Styles.COLOR.RED}>
                  {`제1운전자 등록 불가`}
                </ColorText>
                {`
                ※ 차량 대여 기준 연령 및 운전 경력은 운전면허증 정보에 준합니다.
                `}
                {`주한미군 - 개인여권과 오렌지카드(주한미군 운전면허증) / 여권이 없거나, 영문 철자가 여권과 틀린 경우, 코팅 된 오렌지카드(주한미군 운전면허증)는 렌트 불가함.`}
                <ColorText color={Styles.COLOR.RED}>
                  {`
                  ※ 제1운전자 등록 불가`}
                </ColorText>
                <ColorText color={Styles.COLOR.RED}>
                  {`
                  ※ 주의사항 : 국제면허증과 여권 - 국제운전면허증은 International Driving Permit 만 가능 / 영문 철자가 틀린 경우, 이름이 풀네임으로 표기되지 않은 경우(풀네임 확인 어려운 경우)는 렌트 불가함.
                  `}
                </ColorText>
              </Text>
              <List
                style={{
                  fontWeight: 800,
                }}
              >
                낚시용품 소지고객은 렌트가 불가하오니 이 점 양해바랍니다.
                반려동물 동반 예약은 '펫과 함께' 예약 경로를 통해 이용
                가능합니다.
              </List>
              <List>
                혹시, 운전면허증을 소지하시고 오지 못하신 경우 제주국제공항
                1층에 위치해 있는 자치경찰대 사무실에 가셔서 신분증 제시 후
                운전면허번호 등이 기입된 흰 종이를 받아오시면 대여가능합니다.
              </List>
            </ListWrapper>
          </ParagraphWrapper>
        </Section>
      </SeeMoreSection>

      <SeeMoreSection title='예약변경 및 취소' isMobile={isMobile} onGrey>
        <Section isMobile={isMobile}>
          <ParagraphWrapper>
            <SectionSubTitle>예약변경 및 취소방법</SectionSubTitle>
            <ListWrapper>
              <List>
                렌트카 예약 변경은 예약 취소와 동일하게 취급되며, 당사의 렌트카
                예약현황에 따라 처리가 불가할 수도 있습니다. 예약 변경은 꼭
                예약실로 전화 주시기 바랍니다.
              </List>
              <List>
                렌트카 예약 취소는 예약조회 페이지에서 바로 취소 처리가
                가능합니다.
              </List>
              <List>
                예약변경은 최소 1일 전에 당사로 연락을 주시면 예약변경 처리를
                해드립니다.
              </List>
              <List>
                단, 고객님께서 요청하신 차종 변경이나, 시간 변경은 당사의 렌트카
                예약현황에 따라 처리가 불가능할 수도 있습니다.
              </List>
              <List>
                고객님의 사정으로 인수시간이 늦어진 경우 환불 불가하며, 반납시간
                연장도 불가합니다. 연장을 원하실 경우 예약실로 전화주셔서
                연장요금을 지불해주셔야 합니다.
              </List>
              <List>
                조기 반납의 경우에 해당 시간에 대한 환불금은 없습니다.
              </List>
              <List>
                외국인 또는 국제면허증 소지자의 경우 제2운전자로만 등록이
                가능하며, 반드시 제1운전자로 대한민국 면허증을 소지하신 운전자로
                등록해주셔야 합니다.
              </List>
              <List>
                {`차종변경에 대한 변경 수수료 안내입니다. (변경에 대한 문의는 예약실로 문의해주시기 바랍니다.)
                초기 예약한 차종에 따라 등급별로 수수료 발생합니다. (신규 예약하고자하는 차종 상관없음)
              차량 렌트 요금 차액은 별도로 청구됩니다.

              경차/소형 : 20,000원
              준중형/중형 : 30,000원
              고급/SUV : 40,000원
              수입/승합 : 50,000원
              슈퍼카 : 불가 (24시간 금액 청구)

              차종 변경에 대해서 위 수수료는 기본적으로 발생되고 차량 렌트 요금의 차액은 추가로 청구됩니다.
              위 수수료는 차량 출고전 기준 금액으로, 차량 출고 후 변경에 대한 문의는 수수료도 상이하고 렌트카 상황에 따라 달라지므로 별도로 문의해주시기 바랍니다.
              `}
              </List>
            </ListWrapper>
          </ParagraphWrapper>
        </Section>
        <Section isMobile={isMobile}>
          <ParagraphWrapper>
            <SectionSubTitle>예약금 환불</SectionSubTitle>
            <ListWrapper>
              <List>
                사전통보없이 대여 예정 시간을 1시간 이상 경과하여도 대여계약이
                체결되지 아니한 경우에는 예약은 취소된 것으로 합니다. 이 경우
                예약금은 반환하지 아니합니다.
              </List>
              <List>
                고객이 사정으로 인해 취소시 취소규정에 의해 취소수수료가 부과
                됩니다.
              </List>
              <List>
                대여예정시간 24시간 이내에 예약을 취소하는 경우에는 예약금을
                반환하지 아니합니다.
              </List>
              <List>
                예약을 한 후 천재지변 등 불가항력적인 사유로 계약을 체결할 수
                없게 된 경우에는 회사는 예약금을 고객에게 반환합니다.
              </List>
            </ListWrapper>
          </ParagraphWrapper>
        </Section>
      </SeeMoreSection>

      <SeeMoreSection
        title='자차 특약보험안내'
        isMobile={isMobile}
        onGrey
        defaultOpen={isInsuranceDeepLink}
      >
        <Section isMobile={isMobile}>
          <SectionSubTitle>
            완전면책 상품 - 1회성 소멸상품 가입안내
          </SectionSubTitle>
          <ListWrapper>
            <List>
              <ColorText color={getPrimaryColor(app)}>{`완전면책`}</ColorText>
              {`
                [완전면책 보장내용]
                - 자차 보상한도 무제한
                - 자차 자기부담금 없음
                - 휴차보상료 없음
                - 단독사고 보상 가능
                - 현장출동 등 포함`}
            </List>
          </ListWrapper>
        </Section>
        <Section isMobile={isMobile}>
          <Text>
            <ColorText color={Styles.COLOR.RED}>특약상품에</ColorText>{' '}
            <ColorText color={getPrimaryColor(app)}>슈퍼카</ColorText>{' '}
            <ColorText color={Styles.COLOR.RED}>차량은 제외됩니다.</ColorText>
          </Text>
        </Section>
        <Section isMobile={isMobile}>
          <SectionSubTitle>(필독)자기차량손해면책제도 안내</SectionSubTitle>
          <ListWrapper>
            <List>
              운전자 과실에 의한 차량 손해(손, 망실)는 임차인의 책임입니다.
            </List>
            <List>
              차량대여 계약시 사용자와 렌트카 회사간의 약정에 따라 사용자의
              책임을 감면해주는 제도로서, 보상한도는 회사에서 보상해주는
              차량가액까지이며, 만일 이 금액을 초과하는 사고 발생에 대한 부분은
              고객님께서 보상하셔야 합니다.
            </List>
            <List>
              <ColorText color={getPrimaryColor(app)}>
                {`일반면책 : `}
              </ColorText>
              사고시 자기부담금 50만원과 휴차보상료 50%(사고로 인해 공장에서
              수리하는 동안 영업하지 못한 부분에 대한 손실) 부담, 단독사고 보상
              제외 (보상한도 500만원)
            </List>
            <List>
              <ColorText color={getPrimaryColor(app)}>
                {`고급면책 : `}
              </ColorText>
              자기부담금 100만원, 휴차보상료 없음, 단독사고 보상 포함 (보상한도
              1,500만원). 고급면책는 차종에 따라 운전나이와 경력이 상이합니다.
              '실시간 렌트카' 예약 상세페이지에서 확인바람
            </List>
            <List>
              <ColorText color={getPrimaryColor(app)}>
                {`완전면책 : `}
              </ColorText>
              자차 보상한도 무제한, 자기부담금·휴차보상료 없음, 단독사고 보상,
              현장출동 등 포함
            </List>
            <List>
              <ColorText color={getPrimaryColor(app)}>
                {`자차면책 보상한도 : `}
              </ColorText>
              일반면책 500만원 / 고급면책 1,500만원 / 완전면책 무제한. 한도 초과
              금액은 본인이 부담합니다.
            </List>
            <List>
              <ColorText color={getPrimaryColor(app)}>
                차량손해(자차)면책은 이용자의 선택사항입니다.
              </ColorText>
            </List>
            <List>
              보험 보상과 관련하여 임대차 계약서 뒷면 약관에 기재된 내용을
              숙지바랍니다.
            </List>
          </ListWrapper>
        </Section>
        <Section isMobile={isMobile}>
          <ListWrapper>
            <List>
              <ColorText color={getPrimaryColor(app)}>{`교통법규`}</ColorText>
              {`
              차량 운전자는 교통법규를 준수하셔야 하며, 제반사항은 임차인의 책임입니다. 사고의 경우 도로교통법 위반 시에는 보험보상의
              일부 혜택을 받지 못할 수도 있습니다.`}
            </List>
            <List>
              <ColorText color={getPrimaryColor(app)}>{`계약연장`}</ColorText>
              {`
               계약기간을 연장하여 사용코자 할 경우에는 사전에 동의를 받아야 합니다.
              사전 동의 없이 연장사용 중에 발생한 보험 및 차량 손해에 대해서는 보상이나 면책을 받지 못할 수 있습니다.`}
            </List>
          </ListWrapper>
        </Section>
        <Section isMobile={isMobile}>
          <Text>
            차량사고 발생시 고객 임의수리는 인정되지 않으며 재작업을 요청받으실
            수 있으니 차량사고 발생시에는 저희 회사쪽으로 통보하신 후 적절한
            조치를 받으시길 바랍니다.
          </Text>
          <Text>
            임의수리로 인한 금전적, 시간적 손해로 하여 여행을 망치는 일이 없도록
            유의바랍니다.
          </Text>
        </Section>
        <Section isMobile={isMobile}>
          <SectionSubTitle bold>
            다음의 경우는 보험처리 및 차량손해(자차) 면책처리가 불가능합니다.
          </SectionSubTitle>
          <ListWrapper color={Styles.COLOR.RED}>
            <List>임차인의 고의로 인한 사고</List>
            <List>무면허 운전사고</List>
            <List>
              음주운전, 약물중독운전 사고 - 렌트카를 경기용이나 연습용,
              테스트용으로 사용하던 중 발생한 사고
            </List>
            <List>
              임차인(임차인과 기록된 공동임차인 포함) 이외의 제3자가 렌트카를
              사용하여 발생한 사고
            </List>
            <List>본인 부주의로 인한 차량도난사고</List>
            <List>정원초과로 인한 사고</List>
            <List>사고 후 당사 미보고 사고</List>
            <List>1사고 1건 초과 사고</List>
            <List>우도내에서 발생하는 모든 사고.</List>
            <List>
              타이어펑크 및 타이어파손 비용, 사고견인,
              현장출동(배터리방전,키분실 등)비용은 자차에 적용되지 않습니다.
              (고객부담)
            </List>
            <List>
              단독사고: 일반면책 보상 제외 / 고급면책 자기부담금 100만원
            </List>
            <List>
              완전면책에 가입하였더라도 침수, 해양입수 등 교통사고와 관련 없는
              부분에 대해서는 보장하지 않습니다.
            </List>
          </ListWrapper>
        </Section>
        <Section isMobile={isMobile}>
          <SectionSubTitle bold>
            11대 중과실 항목 위반시에도 면책처리 불가능합니다
          </SectionSubTitle>
          <ListWrapper color={Styles.COLOR.RED}>
            <List>신호 및 지시 위반</List>
            <List>중앙선침범, 불법유턴</List>
            <List>제한속도보다 20KM이상 과속</List>
            <List>앞지르기 방법 위반</List>
            <List>철길건널목 통과방법위반</List>
            <List>횡단보도사고</List>
            <List>무면허운전</List>
            <List>음주운전</List>
            <List>보도 침범 및 보도 횡단방법 위반</List>
            <List>승객추락방지 의무 위반</List>
            <List>어린이보호구역 안전운전 의무 위반</List>
          </ListWrapper>
        </Section>
      </SeeMoreSection>
    </ContentWrapper>
  );
};

export default RentInfo;
